// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { AuthnLevel, InstanceKey } from '../constants';
import type { Condition } from './types';

// Keys are written in full, never composed, so each stays greppable (REQ-031).

export const TEMPLATE_KEYS = {
  summaryLine: 'portal.security.conditions.summary-line',
  otherwiseLine: 'portal.security.conditions.otherwise-line',
  drawerTitle: 'portal.security.conditions.drawer-title',
  reordered: 'portal.security.conditions.reordered',
};

export const RISK_AWARENESS_KEYS = {
  switchLabel: 'portal.security.risk-awareness',
  helper: 'portal.security.conditions.risk-awareness.helper',
  ruleSetLabel: 'portal.security.conditions.risk-awareness.rule-set-label',
  noRuleSet: 'portal.security.conditions.risk-awareness.no-rule-set',
  ruleSetsLoadFailedTitle: 'portal.security.conditions.risk-awareness.rule-sets-load-failed-title',
  ruleSetsLoadFailedMessage:
    'portal.security.conditions.risk-awareness.rule-sets-load-failed-message',
};

export const RISK_LEVEL_LABEL_KEYS = {
  LOW: 'portal.security.conditions.risk-level-option.low',
  MEDIUM: 'portal.security.conditions.risk-level-option.medium',
  HIGH: 'portal.security.conditions.risk-level-option.high',
  ALLOW: 'portal.security.conditions.risk-level-option.allow',
};

export const CONDITION_LABEL_KEYS = {
  'role-is-administrator': 'portal.security.conditions.condition.role-is-administrator',
  'risk-level': 'portal.security.conditions.condition.risk-level',
} satisfies Record<Condition, string>;

// `ICP_MFA_IF_ENROLLED` names a key no bundle defines yet; it is gated on Product copy (REQ-024).
const ACTION_LABEL_KEYS = new Map<string, string>(
  Object.entries({
    [AuthnLevel.ICP_SINGLE_FA]: 'portal.security.options.ICP_SINGLE_FA',
    [AuthnLevel.ICP_MFA]: 'portal.security.options.ICP_MFA',
    [AuthnLevel.ICP_PASSWORDLESS_MFA]: 'portal.security.options.ICP_PASSWORDLESS_MFA',
    [AuthnLevel.ICP_MFA_IF_ENROLLED]: 'portal.security.options.ICP_MFA_IF_ENROLLED',
  } satisfies Record<AuthnLevel, string>),
);

export const actionLabelKey = (action: string | undefined): string | undefined =>
  action === undefined ? undefined : ACTION_LABEL_KEYS.get(action);

export const APPLICATION_LABEL_KEYS = {
  [InstanceKey.USS]: 'portal.security.uss-field-label',
  [InstanceKey.IAM]: 'portal.security.iam-field-label',
};

export const STATUS_LINE_KEYS = {
  'risk-not-applied': 'portal.security.conditions.status.risk-not-applied',
  'risk-awareness-off': 'portal.security.conditions.status.risk-awareness-off',
  'save-first': 'portal.security.conditions.status.save-first',
};

export const EDIT_LINK_KEY = 'portal.security.conditions.edit-link';
export const ITDR_LINK_KEY = 'portal.security.conditions.itdr-link';

export const CONTROL_KEYS = {
  addCondition: 'portal.security.conditions.add-condition',
  save: 'portal.security.conditions.save',
  saving: 'portal.security.conditions.saving',
  cancel: 'portal.security.conditions.cancel',
  conditionLabel: 'portal.security.conditions.condition-label',
  actionLabel: 'portal.security.conditions.action-label',
  removeCondition: 'portal.security.conditions.remove-condition',
  requiredRule: 'portal.security.conditions.required-rule',
  otherwiseCondition: 'portal.security.conditions.otherwise-condition',
};

export const ALERT_KEYS = {
  conflictTitle: 'portal.security.conditions.alert.conflict-title',
  conflictMessage: 'portal.security.conditions.alert.conflict-message',
  saveFailedTitle: 'portal.security.conditions.alert.save-failed-title',
  saveFailedMessage: 'portal.security.conditions.alert.save-failed-message',
  loadFailedTitle: 'portal.security.conditions.alert.load-failed-title',
  loadFailedMessage: 'portal.security.conditions.alert.load-failed-message',
  refresh: 'portal.security.conditions.alert.refresh',
};
